import ButtonArrow from "@components/shared/buttonArrow";
import CheckIcon from "@components/svg/checkIcon";
import AffiliateOfferUrl from "@components/shared/affiliateOfferUrl";
import { AffiliateOfferZone, affiliateOfferZones } from "@lib/shared/config";
import { doesOfferHaveALink, formatPhoneNumber } from "@lib/sharedUtils";
import styles from "./style.module.scss";
import { ListingItem } from "@lib/shared/types";
import { replacePlaceholders } from "@lib/sharedUtils";

type Props = {
    offer?: ListingItem;
    clicked: boolean;
    onClick: () => void;
    featuredOfferMessage?: string;
    isFeatured?: boolean;
    isExternal: boolean;
    slug?: string;
    buttonText?: string;
    actionButtonText?: string;
    revenue?: string | number;
    phoneNumber?: string;
    useActionButton?: boolean;
    onlyButtonAndLinksClickable?: boolean;
    zone?: string;
    useHiddenAttributes: boolean;
    listItemExclusiveOffer?: string;
    listItemReviewLink?: string;
    textUnderButton?: string;
    ratingBlock?: React.ReactNode;
    category?: string;
    region?: string;
    city?: string;
};

export default function OfferCTA({
    offer,
    clicked,
    onClick,
    featuredOfferMessage,
    isFeatured,
    isExternal,
    slug,
    buttonText,
    actionButtonText,
    revenue,
    phoneNumber,
    useActionButton,
    onlyButtonAndLinksClickable,
    zone,
    useHiddenAttributes,
    listItemExclusiveOffer,
    listItemReviewLink,
    textUnderButton,
    ratingBlock,
    category,
    region,
    city,
}: Props) {
    const displayLinkAndPhoneNumber = offer ? doesOfferHaveALink(offer) : false;
    const overlayClass = `before:absolute before:w-full before:h-full before:left-0 before:top-0`;

    const showExclusiveOffer = useHiddenAttributes && listItemExclusiveOffer;

    const showReviewButton =
        useHiddenAttributes && listItemReviewLink && !useActionButton;

    return (
        <div
            className={`${styles["cta"]} ${clicked ? styles["clicked"] : ""}`}
            style={{ textAlign: "center" }}
        >
            {ratingBlock}

            {showExclusiveOffer && (
                <div className={styles["exclusiveOffer"]}>
                    <div className={styles["exclusiveOfferTitle"]}>
                        EXCLUSIVE OFFER
                    </div>
                    <div className={styles["exclusiveOfferDescription"]}>
                        {listItemExclusiveOffer}
                    </div>
                </div>
            )}

            {revenue && <span className="mb-1 block">${revenue}</span>}

            {isExternal ? (
                <a
                    target="_blank"
                    onClick={onClick}
                    href={
                        useHiddenAttributes
                            ? "#"
                            : `/offer/${slug ?? ""}&isExternalOffer=true`
                    }
                    rel="noreferrer"
                    className={`${styles["button"]} ${
                        !onlyButtonAndLinksClickable ? overlayClass : ""
                    }`}
                >
                    {!clicked ? (
                        <span className={styles["main"]}>
                            {replacePlaceholders(buttonText ?? "", {
                                category: category ?? "",
                                region: region ?? "",
                                city: city ?? "",
                            })}
                            <ButtonArrow />
                        </span>
                    ) : (
                        <span className={styles["check"]}>
                            <CheckIcon />
                        </span>
                    )}
                </a>
            ) : (
                <>
                    {showReviewButton ? (
                        <a
                            href={`${listItemReviewLink}`}
                            target="_self"
                            rel="noreferrer"
                            className={`text-center py-3 rounded-lg lg:px-6 ${
                                !displayLinkAndPhoneNumber &&
                                !onlyButtonAndLinksClickable
                                    ? `${overlayClass}`
                                    : ""
                            } ${styles.button}`}
                        >
                            {buttonText}
                            <ButtonArrow />
                        </a>
                    ) : (
                        <>
                            <AffiliateOfferUrl
                                zone={
                                    (zone as AffiliateOfferZone) ??
                                    affiliateOfferZones.PAGE_BUILDER
                                }
                                target={
                                    !phoneNumber || useActionButton
                                        ? "_blank"
                                        : undefined
                                }
                                className={`text-center py-3 rounded-lg lg:px-6 ${
                                    !displayLinkAndPhoneNumber &&
                                    !onlyButtonAndLinksClickable
                                        ? `${overlayClass}`
                                        : ""
                                } ${styles.button}`}
                                offer={offer}
                                customHref={
                                    phoneNumber && !useActionButton
                                        ? `tel:${phoneNumber ?? ""}`
                                        : undefined
                                }
                            >
                                <span>
                                    {useActionButton && actionButtonText
                                        ? actionButtonText
                                        : formatPhoneNumber(phoneNumber) ??
                                          buttonText}

                                    <ButtonArrow />
                                </span>
                            </AffiliateOfferUrl>

                            {displayLinkAndPhoneNumber && (
                                <AffiliateOfferUrl
                                    zone={
                                        (zone as AffiliateOfferZone) ??
                                        affiliateOfferZones.PAGE_BUILDER
                                    }
                                    className={`text-left lg:text-center py-3 rounded-lg ${styles.link}`}
                                    offer={offer}
                                    target="_blank"
                                >
                                    <span>View Deals</span>
                                </AffiliateOfferUrl>
                            )}
                        </>
                    )}
                </>
            )}
            {textUnderButton && (
                <div className={styles["txtUnderBtn"]}>{textUnderButton}</div>
            )}
            {isFeatured && (
                <span className={styles["featured-offer-message"]}>
                    {featuredOfferMessage}
                </span>
            )}
        </div>
    );
}
